import React, { useMemo, useReducer, useState } from 'react';

// Styles & Assets
import styles from './ScoresAlertsDialog.module.css';

// Components
import CancelConfirmButtons from '../../UI/CancelConfirmButtons/CancelConfirmButtons';
import ScoreRule from '../ScoreRule/ScoreRule';
import AlertRule from '../AlertRule/AlertRule';

// Hooks
import useHttp from '../../../hooks/useHttp';
import { useToast } from '../../../hooks/useToast';

// reducers
import { scoreRulesReducer, alertRulesReducer } from '../../../reducers/score-reducers';

// Languages
import { useTranslation } from 'react-i18next';

// Interfaces
import { IAlertRule, IScoreRule, Score } from '../../../interfaces/Score';
import { Poll } from '../../../interfaces/Poll';
import { getEnvironmentApiUrl } from '../../../services/General';

interface Props {
    defaultScores: IScoreRule[];
    scoreID: number | undefined;
    defaultAlerts: IAlertRule[];
    poll: Poll;
    setShow: React.Dispatch<React.SetStateAction<boolean>>;
    addAlerts: (alerts: IAlertRule[]) => void;
    addScore: (score: Score | number) => void;
}

const ScoresAlertsDialog: React.FC<Props> = ({
    defaultScores,
    scoreID,
    defaultAlerts,
    poll,
    setShow,
    addAlerts,
    addScore
}) => {
    const { t } = useTranslation();
    const toast = useToast();

    const [{ scoreRules, scoreErrors, deletedIDs: scoreDeletedIDs }, dispatchScoreRules] = useReducer(
        scoreRulesReducer,
        { scoreRules: defaultScores || [], scoreErrors: [], deletedIDs: [] }
    );

    const [alerts, dispatchAlertRules] = useReducer(alertRulesReducer, defaultAlerts || []);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);

    const { sendRequest: sendScores } = useHttp<Score>();
    const { sendRequest: sendAlertRules } = useHttp<IAlertRule[]>();
    const { sendRequest: deleteRules } = useHttp();

    const addScoreRule = () => dispatchScoreRules({ type: 'ADD_RULE' });
    const addAlertRule = () => dispatchAlertRules({ type: 'ADD_RULE' });

    const hasValidationError = useMemo(() => {
        const errScores = scoreErrors.length !== 0 || scoreErrors.some(err => err.error !== undefined);
        const errAlerts = alerts.some(
            a => isNaN(a.value) || isNaN(a.consecutive) || !Number.isInteger(a.consecutive) || a.consecutive < 1
        );

        return errScores || errAlerts;
    }, [alerts, scoreErrors]);

    const showError = attemptedSubmit && hasValidationError;

    const onConfirm = () => {
        setAttemptedSubmit(true);

        if (hasValidationError) {
            return;
        }

        const scoresNoUUID = scoreRules.map(rule => {
            const newScore: Partial<IScoreRule> = { ...rule };
            if (typeof newScore.id === 'string') delete newScore.id;
            return newScore;
        });

        const alertsNoUUID = alerts.map(alert => {
            const newAlert: Partial<IAlertRule> = { ...alert };
            if (typeof newAlert.id === 'string') delete newAlert.id;
            return newAlert;
        });

        const postScore = () => {
            if (scoresNoUUID.length > 0) {
                sendScores({
                    url: getEnvironmentApiUrl() + 'inquiry/score',
                    method: 'POST',
                    body: { score_rules: scoresNoUUID, inquiry_id: poll.id, inquiry_name: poll.name },
                }, (score: Score) => {
                    addScore(score);
                    toast.success(t('success_creating_score'));
                }, () => toast.error(t('error_creating_score')));
            } else if (scoreID !== undefined) {
                addScore(scoreID);
            }
        };

        if (scoreDeletedIDs.length === 0) {
            postScore();
        } else {
            deleteRules({
                url: getEnvironmentApiUrl() + 'inquiry/score',
                method: 'DELETE',
                body: { score_id: scoreID, score_rule_ids: scoreDeletedIDs },
            }, () => postScore());
        }

        if (alertsNoUUID.length > 0) {
            sendAlertRules({
                url: getEnvironmentApiUrl() + 'inquiry/alert',
                method: 'POST',
                body: { inquiry_alert: alertsNoUUID, inquiry_id: poll.id },
            }, (alertRules: IAlertRule[]) => {
                addAlerts(alertRules);
                toast.success(t('success_creating_alert'));
            }, () => toast.error(t('error_creating_alert')));
        } else {
            addAlerts([]);
        }

        setShow(false);
    };

    return (
        <div className='dialog'>
            <h1 className={`dialogTitle ${styles.title}`}>{t('scores_and_alerts')}</h1>

            <div className={styles.subtitleContainer}>
                <h2 className='dialogSubTitle'>{t('rules_score')}</h2>
                <button className={styles.buttonAdd} onClick={addScoreRule}>
                    {t('add')} +
                </button>
            </div>

            <div className={styles.rulesContainer}>
                {scoreRules.map(rule => (
                    <ScoreRule
                        key={rule.id}
                        rule={rule}
                        dispatchScoreRules={dispatchScoreRules}
                        error={scoreErrors.find(r => r.id === rule.id)?.error}
                    />
                ))}
            </div>

            <div className={styles.subtitleContainer}>
                <h2 className='dialogSubTitle'>{t('alerts')}</h2>
                <button className={styles.buttonAdd} onClick={addAlertRule}>
                    {t('add')} +
                </button>
            </div>

            <div className={styles.rulesContainer}>
                {alerts.map(rule => !rule.deleted && (
                    <AlertRule
                        key={rule.id}
                        questions={poll.questions || []}
                        rule={rule}
                        dispatchAlertRules={dispatchAlertRules}
                    />
                ))}
            </div>

            {showError && (
                <p className='errorMsg' style={{ textAlign: 'center' }}>
                    {t('check_for_an_error')}
                </p>
            )}

            <CancelConfirmButtons
                onCancel={() => setShow(false)}
                onConfirm={onConfirm}
            />
        </div>
    );
};

export default ScoresAlertsDialog;